// eslint-disable-next-line no-restricted-imports
import { LicenseInfo, ThemeExtensionContainer } from '@goshippo/components';
import { FullStoryProvider, SentryInitProps, SentryProvider } from '@goshippo/utilities';
import { BrowserTracing } from '@sentry/browser';
import momentLocaleLoader from '~/components/momentLocaleLoader';
import { ConnectedRouter } from 'connected-react-router';
import { History } from 'history';
import { hot } from 'react-hot-loader';
import { compose } from 'redux';
import FullStoryMonitor from '~/utils/fullstory/FullStoryMonitor';
import i18n from '~/utils/i18n';
import { SegmentMonitor } from '~/utils/segment';
import { SiftMonitor } from '~/utils/sift';
import { NewRelicMonitor } from './utils/newRelic/NewRelicMonitor';
import TrackingContainer from '~/utils/TrackingHelper/TrackingContainer';
import { I18nextProvider } from '~/utils/translate';
import ZendeskHelpContainer from '~/utils/ZendeskHelp/ZendeskHelpContainer';
import AppRoutes from './AppRoutes';
import { FeatureFlagsProvider } from '~/utils/featureflagsV2';
import { optimizelyClient } from '~/utils/featureflagsV2/FeatureFlagsProvider';

// It's expected that this will be in source control
// We call it once so our components are registered
LicenseInfo.setLicenseKey(
  '31b6b425575820bbe479c9218d591364T1JERVI6Mzg2MjAsRVhQSVJZPTE2Nzc0MzczNDgwMDAsS0VZVkVSU0lPTj0x',
);
export interface AppProps {
  history: History;
}

const sentryInitProps: SentryInitProps | undefined =
  CONFIG.sentry.dsn && CONFIG.environmentName
    ? {
        dsn: CONFIG.sentry.dsn,
        release: `feron@${APP_VERSION}`,
        integrations: [new BrowserTracing()],
        tracesSampleRate: 0.001,
        environment: CONFIG.environmentName,
        organization: CONFIG.sentry.organization,
        useFullStoryIntegration: CONFIG.environmentName === 'production',
        debug: ['dev-qa', 'dynamic'].includes(CONFIG.environmentName),
        beforeSend: function (event, _) {
          // filter out UnhandledRejection errors for Object Not Found Matching Id
          // from https://github.com/getsentry/sentry-javascript/issues/3440
          if (event?.exception?.values?.length === 1) {
            const e = event.exception.values[0];

            if (
              e.type === 'UnhandledRejection' &&
              e.value?.includes(
                'Non-Error promise rejection captured with value: Object Not Found Matching Id',
              )
            ) {
              return null;
            }
          }

          return event;
        },
        // from https://docs.sentry.io/platforms/javascript/configuration/filtering/#decluttering-sentry
        // and gist: https://gist.github.com/Chocksy/e9b2cdd4afc2aadc7989762c4b8b495a
        ignoreErrors: [
          // Random plugins/extensions
          'top.GLOBALS',
          // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
          'originalCreateNotification',
          'canvas.contentDocument',
          'MyApp_RemoveAllHighlights',
          'http://tt.epicplay.com',
          "Can't find variable: ZiteReader",
          'jigsaw is not defined',
          'ComboSearch is not defined',
          'http://loading.retry.widdit.com/',
          'atomicFindClose',
          // Facebook borked
          'fb_xd_fragment',
          // ISP "optimizing" proxy - `Cache-Control: no-transform` seems to reduce this. (thanks @acdha)
          // See http://stackoverflow.com/questions/4113268/how-to-stop-javascript-injection-from-vodafone-proxy
          'bmi_SafeAddOnload',
          'EBCallBackMessageReceived',
          // See http://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
          'conduitPage',
          // Generic error code from errors outside the security sandbox
          // You can delete this if using raven.js > 1.0, which ignores these automatically.
          'Script error.',
          // Avast extension error
          '_avast_submit',
          // Shippo-added goes below
          'ChunkLoadError',
          'A network error occurred',
          /Failed to fetch/, // force exact match
        ],
        denyUrls: [
          // Google Adsense
          /pagead\/js/i,
          // Facebook flakiness
          /graph\.facebook\.com/i,
          // Facebook blocked
          /connect\.facebook\.net\/en_US\/all\.js/i,
          // Woopra flakiness
          /eatdifferent\.com\.woopra-ns\.com/i,
          /static\.woopra\.com\/js\/woopra\.js/i,
          // Chrome extensions
          /extensions\//i,
          /^chrome:\/\//i,
          // Other plugins
          /127\.0\.0\.1:4001\/isrunning/i, // Cacaoweb
          /webappstoolbarba\.texthelp\.com\//i,
          /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
        ],
      }
    : undefined;

const App = (props: AppProps) => {
  return (
    <I18nextProvider i18n={i18n}>
      <FullStoryProvider fullStoryOpts={CONFIG.fullStory}>
        <SentryProvider sentryInitOpts={sentryInitProps}>
          <FeatureFlagsProvider optimizelyClient={optimizelyClient}>
            <FullStoryMonitor />
            <SegmentMonitor />
            <SiftMonitor />
            <NewRelicMonitor />
            <ZendeskHelpContainer>
              <ConnectedRouter history={props.history}>
                <ThemeExtensionContainer>
                  <TrackingContainer>
                    <AppRoutes />
                  </TrackingContainer>
                </ThemeExtensionContainer>
              </ConnectedRouter>
            </ZendeskHelpContainer>
          </FeatureFlagsProvider>
        </SentryProvider>
      </FullStoryProvider>
    </I18nextProvider>
  );
};

const AppWithHotModule = compose(hot(module), momentLocaleLoader)(App);

export default AppWithHotModule;
